/**
 * QuickFilters Component
 * 
 * Preset filter buttons for common filter configurations.
 */

import React from 'react';
import {
  HStack,
  Button,
  Wrap,
  WrapItem,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiStar, FiAlertTriangle, FiCalendar, FiZap, FiUsers, FiDollarSign } from 'react-icons/fi';

// Predefined quick filters for contacts
const CONTACT_QUICK_FILTERS = [
  {
    id: 'active',
    label: 'Active Only',
    icon: FiZap,
    filters: [{ field: 'isActive', operator: 'is_true', value: true }],
  },
  {
    id: 'favorites',
    label: 'Favorites',
    icon: FiStar,
    filters: [{ field: 'isFavorite', operator: 'is_true', value: true }],
  },
  {
    id: 'borrowers',
    label: 'Borrowers',
    icon: FiUsers,
    filters: [{ field: 'contactTypes', operator: 'contains_any', value: ['borrower'] }],
  },
  {
    id: 'realtors',
    label: 'Realtors',
    icon: FiUsers,
    filters: [{ field: 'contactTypes', operator: 'contains_any', value: ['real-estate-agent'] }],
  },
  {
    id: 'has-deals',
    label: 'Has Deals',
    icon: FiDollarSign,
    filters: [{ field: 'totalDeals', operator: 'gt', value: 0 }],
  },
  {
    id: 'recent',
    label: 'Added Last 30 Days',
    icon: FiCalendar,
    filters: [{ field: 'createdAt', operator: 'date_relative', value: 'last_30_days' }],
  },
];

// Predefined quick filters for deals
const DEAL_QUICK_FILTERS = [
  {
    id: 'new-leads',
    label: 'New Leads',
    icon: FiZap,
    filters: [{ field: 'stage', operator: 'in', value: ['lead-new'] }],
  },
  {
    id: 'at-risk',
    label: 'At Risk',
    icon: FiAlertTriangle,
    colorScheme: 'red',
    filters: [{ field: 'isAtRisk', operator: 'is_true', value: true }],
  },
  {
    id: 'closing-this-month',
    label: 'Closing This Month',
    icon: FiCalendar,
    filters: [{ field: 'estimatedClosingDate', operator: 'date_relative', value: 'this_month' }],
  },
  {
    id: 'high-value',
    label: 'High Value ($400k+)',
    icon: FiDollarSign,
    colorScheme: 'green',
    filters: [{ field: 'amount', operator: 'gte', value: 400000 }],
  },
  {
    id: 'active-pipeline',
    label: 'Active Pipeline',
    icon: FiZap,
    // Active = all lead + loan workflow stages (Arive-aligned + legacy slugs for backwards compat)
    filters: [{ field: 'stage', operator: 'in', value: [
      'lead-new', 'lead-contacted', 'lead-quoted',
      'loan-application-intake', 'loan-qualification', 'loan-preapproved',
      'loan-setup', 'loan-disclosure-sent', 'loan-submitted',
      'loan-approved-conditions', 'loan-resubmittal',
      'loan-clear-to-close', 'loan-docs-out', 'loan-docs-signed',
      // Legacy slugs — Phase A backwards compatibility
      'lead-qualified', 'lead-qualified-hot', 'lead-contacted-hot',
      'loan-app-intake', 'loan-processing',
    ] }],
  },
  {
    id: 'recent',
    label: 'Added Last 7 Days',
    icon: FiCalendar,
    filters: [{ field: 'createdAt', operator: 'date_relative', value: 'last_7_days' }],
  },
];

/**
 * QuickFilters - Preset filter buttons
 *
 * Single-select by default (clicking the active chip clears it). Pass
 * `multiSelect` + `activeFilterIds` to let several chips toggle on together —
 * the parent owns the selection and merge logic.
 */
export default function QuickFilters({
  entityType = 'contacts',
  activeFilterId = null,
  activeFilterIds = null,
  multiSelect = false,
  onQuickFilterClick,
  customQuickFilters = [],
}) {
  const activeBg = useColorModeValue('blue.100', 'blue.800');
  
  // Get quick filters for entity type
  const defaultFilters = entityType === 'deals' ? DEAL_QUICK_FILTERS : CONTACT_QUICK_FILTERS;
  const quickFilters = [...defaultFilters, ...customQuickFilters];

  const activeIds = activeFilterIds || (activeFilterId ? [activeFilterId] : []);

  const handleClick = (quickFilter) => {
    if (multiSelect) {
      // Parent toggles selection; always report the clicked chip
      onQuickFilterClick(quickFilter.id, quickFilter.filters);
    } else if (activeIds.includes(quickFilter.id)) {
      // Clicking active filter clears it
      onQuickFilterClick(null, []);
    } else {
      onQuickFilterClick(quickFilter.id, quickFilter.filters);
    }
  };
  
  return (
    <Wrap spacing={2}>
      {quickFilters.map(quickFilter => {
        const isActive = activeIds.includes(quickFilter.id);
        const Icon = quickFilter.icon;
        
        return (
          <WrapItem key={quickFilter.id}>
            <Button
              size="sm"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? (quickFilter.colorScheme || 'blue') : 'gray'}
              leftIcon={Icon ? <Icon /> : undefined}
              onClick={() => handleClick(quickFilter)}
              bg={isActive ? activeBg : undefined}
            >
              {quickFilter.label}
            </Button>
          </WrapItem>
        );
      })}
    </Wrap>
  );
}

// Export preset filters for external use
export { CONTACT_QUICK_FILTERS, DEAL_QUICK_FILTERS };
